import {HttpError} from '@app/features/platform/types/EndpointError';
import * as Moq from '@moq/net';
import type {Effect} from '@moq/signals';
import {ExternalE2EEKeyProvider, MediaCrypto, registerKeyedRoom} from './e2ee';
import {Emitter} from './emitter';
import {
	ConnectionQuality,
	ConnectionState,
	ParticipantEvent,
	RoomEvent,
	type SubscriptionStatus,
	TrackEvent,
} from './enums';
import {type DataMessage, discover, fromBase64, KIND, type Kind, LocalMedia, RemoteBroadcast} from './moq';
import type {RoomConnectOptions, RoomOptions} from './options';
import {LocalParticipant, type Participant, RemoteParticipant} from './participant';
import {setPlaybackContext} from './playoutWorklet';
import {RemoteTrackPublication} from './publication';
import {RemoteAudioTrack, type RemoteTrack, RemoteVideoTrack, Track} from './track';

const CONNECT_TIMEOUT_MS = 15_000;

export enum DisconnectReason {
	UNKNOWN_REASON = 0,
	CLIENT_INITIATED = 1,
	DUPLICATE_IDENTITY = 2,
	SERVER_SHUTDOWN = 3,
	PARTICIPANT_REMOVED = 4,
	ROOM_DELETED = 5,
	STATE_MISMATCH = 6,
	JOIN_FAILURE = 7,
}

/**
 * What the relay URL cannot say by itself, configured per voice server in
 * the admin API (docs/strands/voiceservers.md): a WebSocket fallback on
 * another host or port (`@moq/net` otherwise just maps https -> wss), and
 * the SHA-256 fingerprint of a self-signed certificate (otherwise the
 * fallback is an insecure fetch of /certificate.sha256).
 */
interface RelayHints {
	ws?: string;
	fp?: string;
}

/**
 * The connection target: the relay URL from VOICE_SERVER_UPDATE
 * (https://relay/<root>?jwt=<token>#<identity>[&ws=…][&fp=…]). The fragment
 * carries our voice identity and those hints; browsers never send fragments,
 * so the relay never sees any of it. The values are written as they are
 * (internal/voice/moq.go relayFragment), so nothing is decoded here.
 */
function parseEndpoint(endpoint: string, token: string): {url: URL; identity: string; hints: RelayHints} {
	const url = new URL(endpoint);
	const [rawIdentity, ...rest] = url.hash.replace(/^#/, '').split('&');
	const hints: RelayHints = {};
	for (const part of rest) {
		const eq = part.indexOf('=');
		if (eq <= 0) continue;
		const key = part.slice(0, eq);
		if (key === 'ws' || key === 'fp') hints[key] = part.slice(eq + 1);
	}
	url.hash = '';
	if (!url.searchParams.has('jwt') && token) url.searchParams.set('jwt', token);
	const identity = decodeURIComponent(rawIdentity);
	return {url, identity: identity || identityFromToken(token), hints};
}

/** The connection options a relay's hints ask for. */
function relayProps(hints: RelayHints): Pick<Moq.Connection.ReloadProps, 'websocket' | 'webtransport'> {
	const props: Pick<Moq.Connection.ReloadProps, 'websocket' | 'webtransport'> = {};
	if (hints.ws) {
		try {
			props.websocket = {url: new URL(hints.ws)};
		} catch {
			console.warn('[parley/rtc] voice server has an unusable websocket url', hints.ws);
		}
	}
	// Hex is what the relay reports and what @moq/net accepts here.
	if (hints.fp) props.webtransport = {serverCertificateHashes: [{value: hints.fp}]};
	return props;
}

function tokenPayload(token: string): {put?: string | Array<string>; exp?: number; iat?: number} {
	try {
		return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
	} catch {
		return {};
	}
}

function identityFromToken(token: string): string {
	const payload = tokenPayload(token);
	const put = Array.isArray(payload.put) ? payload.put[0] : payload.put;
	return (put ?? '').replace(/\/+$/, '');
}

/** Refresh the relay token before a reconnect when it expires within this. */
const TOKEN_REFRESH_MARGIN_MS = 60_000;

/**
 * Also refresh before any reconnect when the last refresh is older than
 * this: the relay closes a session whose token the API revoked (moderation:
 * server mute, move, lost permissions), and the old token would be refused.
 */
const TOKEN_RECHECK_MS = 3_000;

/** Whether a relay token grants publishing (`put`). */
function tokenCanPublish(token: string): boolean {
	const {put} = tokenPayload(token);
	return Array.isArray(put) ? put.length > 0 : !!put;
}

/** When a token should be refreshed: a minute before expiry, or half-way for short-lived tokens. */
function tokenRefreshAt(token: string): number {
	const {exp, iat} = tokenPayload(token);
	if (!exp) return 0;
	const lifetime = iat ? (exp - iat) * 1000 : TOKEN_REFRESH_MARGIN_MS * 2;
	return exp * 1000 - Math.min(TOKEN_REFRESH_MARGIN_MS, lifetime / 2);
}

/** One remote participant's broadcasts and publications. */
class RemotePeer {
	readonly participant: RemoteParticipant;
	readonly broadcasts = new Map<Kind, RemoteBroadcast>();
	readonly publications = new Map<Track.Source, RemoteTrackPublication>();
	preview: {audio?: boolean; video?: boolean; screen?: boolean} = {};
	catalogs = new Map<Kind, {video: boolean; audio: boolean}>();

	constructor(identity: string) {
		this.participant = new RemoteParticipant(identity, identity);
	}
}

export interface RoomEngine {
	client: {rtt?: number};
	pcManager?: {publisher?: RTCPeerConnection; subscriber?: RTCPeerConnection; mode?: string};
}

export class Room extends Emitter<string> {
	readonly options: RoomOptions;
	state: ConnectionState = ConnectionState.Disconnected;
	name = '';
	localParticipant: LocalParticipant;
	remoteParticipants = new Map<string, RemoteParticipant>();
	activeSpeakers: Array<Participant> = [];
	/**
	 * LiveKit exposes its WebRTC engine here. MoQ has no peer connections, so
	 * pcManager stays unset and the stats code reports no per-track stats.
	 */
	readonly engine: RoomEngine = {client: {}};
	canPlaybackAudio = true;
	isE2EEEnabled = false;
	#connection?: Moq.Connection.Reload;
	#local?: LocalMedia;
	#discovery?: Effect;
	#peers = new Map<string, RemotePeer>();
	/** Voice connections the gateway said left; the relay may still announce them for a few seconds. */
	#departed = new Set<string>();
	#statusDispose?: () => void;
	#tokenRefreshAt = 0;
	#lastTokenRefresh = 0;
	#refreshingToken = false;
	#activeDevices = new Map<MediaDeviceKind, string>();
	#everConnected = false;
	/** End-to-end encryption of this room's media (e2ee.ts). */
	readonly #crypto: MediaCrypto;
	#unregisterKeys?: () => void;

	constructor(options: RoomOptions = {}) {
		super();
		this.options = options;
		// Fluxer passes {keyProvider, worker} when the gateway sent an e2ee_key;
		// parley encrypts on the main thread (WebCrypto), without a worker.
		const provider = (options.e2ee as {keyProvider?: unknown} | undefined)?.keyProvider;
		this.#crypto = new MediaCrypto(provider instanceof ExternalE2EEKeyProvider ? provider : undefined);
		this.localParticipant = new LocalParticipant('', '');
		this.#applyDefaults();
		if (options.audioOutput?.deviceId) this.#activeDevices.set('audiooutput', options.audioOutput.deviceId);
		// Remote audio mixes in the app's shared voice context when it hands
		// one over, like LiveKit's webAudioMix (playoutWorklet.ts).
		const mix = options.webAudioMix;
		if (mix && typeof mix === 'object' && mix.audioContext) setPlaybackContext(mix.audioContext);
	}

	/** The mixing context; the app's output device sync sets its sink. */
	get audioContext(): AudioContext | undefined {
		const mix = this.options.webAudioMix;
		return mix && typeof mix === 'object' ? mix.audioContext : undefined;
	}

	#applyDefaults(): void {
		this.localParticipant.audioCaptureDefaults = this.options.audioCaptureDefaults ?? {};
		this.localParticipant.videoCaptureDefaults = this.options.videoCaptureDefaults ?? {};
		this.localParticipant.publishDefaults = this.options.publishDefaults ?? {};
		this.localParticipant.roomEmit = (event, ...args) => this.emit(event, ...args);
	}

	get numParticipants(): number {
		return this.remoteParticipants.size + 1;
	}

	get numPublishers(): number {
		return [...this.remoteParticipants.values()].filter((p) => p.trackPublications.size > 0).length;
	}

	#setState(state: ConnectionState): void {
		if (this.state === state) return;
		this.state = state;
		this.emit(RoomEvent.ConnectionStateChanged, state);
	}

	async connect(endpoint: string, token: string, _options?: RoomConnectOptions): Promise<void> {
		if (this.#connection) throw new Error('room already connected');
		const {url, identity, hints} = parseEndpoint(endpoint, token);
		if (!identity) throw new Error('voice endpoint carries no participant identity');
		this.name = url.pathname.replace(/^\/+/, '');
		this.localParticipant.identity = identity;
		this.localParticipant.sid = identity;
		this.#setState(ConnectionState.Connecting);

		const connection = new Moq.Connection.Reload({url, enabled: true, ...relayProps(hints)});
		this.#connection = connection;
		this.#tokenRefreshAt = tokenRefreshAt(url.searchParams.get('jwt') ?? '');
		this.#local = new LocalMedia(
			connection,
			identity,
			(speaking, level) => {
				this.localParticipant.setSpeaking(speaking, level);
				this.#updateActiveSpeakers();
			},
			this.#crypto,
		);
		// Key rotations (VOICE_E2EE_KEY_UPDATE) find this room by its connection.
		this.#unregisterKeys = registerKeyedRoom({
			connectionId: () => /^user_\d+_(.+)$/.exec(this.localParticipant.identity)?.[1],
			e2eeKeyProvider: () => this.#crypto.provider,
		});
		this.localParticipant.publisher = this.#local;
		this.#applyPublishRight(url.searchParams.get('jwt') ?? token);
		for (const pub of this.localParticipant.getTrackPublications()) this.#local.publish(pub);

		await new Promise<void>((resolve, reject) => {
			const timer = setTimeout(() => {
				dispose();
				this.#teardown();
				this.#setState(ConnectionState.Disconnected);
				reject(new Error('could not reach the voice relay'));
			}, CONNECT_TIMEOUT_MS);
			const dispose = connection.status.subscribe((status) => {
				if (status !== 'connected') return;
				clearTimeout(timer);
				queueMicrotask(() => dispose());
				resolve();
			});
		});

		this.#everConnected = true;
		this.localParticipant.setConnectionQuality(ConnectionQuality.Excellent);
		this.#setState(ConnectionState.Connected);
		this.emit(RoomEvent.Connected);
		const statusDispose = connection.status.subscribe((status) => this.#onStatus(status));
		// moq-lite PROBE round trips feed the client's latency display.
		this.engine.client.rtt = connection.probe.peek()?.rtt;
		const probeDispose = connection.probe.subscribe((probe) => {
			this.engine.client.rtt = probe?.rtt;
		});
		this.#statusDispose = () => {
			statusDispose();
			probeDispose();
		};
		this.#discovery = discover(connection, identity, {
			added: (id, kind, path) => this.#addBroadcast(id, kind, path),
			removed: (id, kind) => this.#removeBroadcast(id, kind),
		});
	}

	#onStatus(status: 'connecting' | 'connected' | 'disconnected'): void {
		if (!this.#everConnected || this.state === ConnectionState.Disconnected) return;
		if (status === 'connected' && this.state === ConnectionState.Reconnecting) {
			this.#setState(ConnectionState.Connected);
			this.localParticipant.setConnectionQuality(ConnectionQuality.Excellent);
			this.emit(RoomEvent.Reconnected);
		} else if (status !== 'connected' && this.state === ConnectionState.Connected) {
			this.#setState(ConnectionState.Reconnecting);
			this.localParticipant.setConnectionQuality(ConnectionQuality.Lost);
			this.emit(RoomEvent.Reconnecting);
		}
		if (status !== 'connected') void this.#refreshTokenIfExpiring();
	}

	/**
	 * A token without `put` (server mute, deafen, timeout, no SPEAK/STREAM):
	 * drop the local publication, like LiveKit does when the server revokes
	 * canPublish; publish again once a token grants it.
	 */
	#applyPublishRight(token: string): void {
		const canPublish = tokenCanPublish(token);
		this.#local?.setAllowed(canPublish);
		const permissions = this.localParticipant.permissions;
		if (permissions && permissions.canPublish !== canPublish) {
			const previous = {...permissions};
			permissions.canPublish = canPublish;
			this.emit(RoomEvent.ParticipantPermissionsChanged, previous, this.localParticipant);
		}
	}

	/**
	 * The relay checks the JWT only when a client connects, and the gateway
	 * issues it only on join. Before reconnecting with an (almost) expired
	 * token, ask the API for a fresh one (parley extension). A 403/404 means
	 * the voice connection is gone (left, kicked, lost access): stop.
	 */
	async #refreshTokenIfExpiring(): Promise<void> {
		const connection = this.#connection;
		const now = Date.now();
		if (!connection || this.#refreshingToken) return;
		if (now < this.#tokenRefreshAt && now - this.#lastTokenRefresh < TOKEN_RECHECK_MS) return;
		const connectionId = /^user_\d+_(.+)$/.exec(this.localParticipant.identity)?.[1];
		if (!connectionId) return;
		this.#refreshingToken = true;
		try {
			// Loaded on use: like livekit-client, importing the rtc module must not
			// pull the app's REST stack (and its translated messages) in with it.
			const {http} = await import('@app/features/platform/transport/RestTransport');
			const res = await http.post<{endpoint: string; token: string}>(
				`/voice/connections/${encodeURIComponent(connectionId)}/token`,
			);
			if (this.#connection !== connection) return;
			const {url, hints} = parseEndpoint(res.body.endpoint, res.body.token);
			// The refresh may answer with an edited server (new fallback or
			// certificate); the next attempt of the reload loop uses them.
			const props = relayProps(hints);
			connection.websocket = props.websocket;
			connection.webtransport = props.webtransport;
			this.#tokenRefreshAt = tokenRefreshAt(res.body.token);
			this.#lastTokenRefresh = Date.now();
			this.#applyPublishRight(res.body.token);
			connection.url.set(url);
		} catch (error) {
			if (error instanceof HttpError && (error.status === 403 || error.status === 404)) {
				if (this.#connection !== connection) return;
				this.#teardown();
				this.#setState(ConnectionState.Disconnected);
				this.emit(RoomEvent.Disconnected, DisconnectReason.PARTICIPANT_REMOVED);
				return;
			}
			console.warn('[parley/rtc] voice token refresh failed; retrying on the next reconnect', error);
		} finally {
			this.#refreshingToken = false;
		}
	}

	#peer(identity: string): RemotePeer {
		let peer = this.#peers.get(identity);
		if (!peer) {
			peer = new RemotePeer(identity);
			this.#peers.set(identity, peer);
			this.remoteParticipants.set(identity, peer.participant);
			peer.participant.setConnectionQuality(ConnectionQuality.Good);
			this.emit(RoomEvent.ParticipantConnected, peer.participant);
		}
		return peer;
	}

	#addBroadcast(identity: string, kind: Kind, path: Moq.Path.Valid): void {
		if (this.#departed.has(identity)) return;
		const connection = this.#connection;
		if (!connection) return;
		// Which broadcast of a participant arrived (camera.hang, screen.hang):
		// the one line that says whether a screen share reaches this client.
		console.info(`[parley/rtc] broadcast ${path}`);
		const peer = this.#peer(identity);
		peer.broadcasts.get(kind)?.close();
		const broadcast = new RemoteBroadcast(
			kind,
			identity,
			path,
			connection,
			{
				videoTrack: (k, track, dims) => this.#remoteVideo(peer, k, track, dims),
				audioTrack: (k, track) => this.#remoteAudio(peer, k, track),
				catalog: (k, video, audio) => {
					peer.catalogs.set(k, {video, audio});
					this.#syncPublications(peer);
				},
				preview: (preview) => {
					peer.preview = preview;
					this.#syncPublications(peer);
				},
				user: (name) => {
					if (name && peer.participant.name !== name) {
						peer.participant.name = name;
						peer.participant.emit(ParticipantEvent.ParticipantNameChanged, name);
						this.emit(RoomEvent.ParticipantNameChanged, name, peer.participant);
					}
				},
				speaking: (speaking, level) => {
					if (k(kind)) {
						peer.participant.setSpeaking(speaking, level);
						this.#updateActiveSpeakers();
					}
				},
				data: (message) => this.#onData(peer, message),
			},
			this.#crypto,
		);
		peer.broadcasts.set(kind, broadcast);
		broadcast.setAudioWanted(true);
		this.#syncPublications(peer);
	}

	/**
	 * The gateway says this voice connection left (its voice state has no
	 * channel any more). The relay only drops its broadcasts when the
	 * publisher's session times out - seconds later, with the participant
	 * still listed and audible until then (moq-dev/moq#2401). So leave now,
	 * faded out, and ignore the relay's late announcements of it.
	 */
	dropConnection(identity: string): void {
		this.#departed.add(identity);
		const peer = this.#peers.get(identity);
		if (!peer) return;
		for (const kind of [...peer.broadcasts.keys()]) this.#removeBroadcast(identity, kind);
	}

	/** The gateway says this connection is in our channel (again). */
	readmitConnection(identity: string): void {
		this.#departed.delete(identity);
	}

	#removeBroadcast(identity: string, kind: Kind): void {
		const peer = this.#peers.get(identity);
		if (!peer) return;
		console.info(`[parley/rtc] broadcast gone ${identity}/${kind}.hang`);
		peer.broadcasts.get(kind)?.closeGracefully();
		peer.broadcasts.delete(kind);
		peer.catalogs.delete(kind);
		this.#syncPublications(peer);
		if (peer.broadcasts.size === 0) {
			this.#peers.delete(identity);
			this.remoteParticipants.delete(identity);
			peer.participant.setSpeaking(false, 0);
			this.#updateActiveSpeakers();
			this.emit(RoomEvent.ParticipantDisconnected, peer.participant);
		}
	}

	/** Reconcile a peer's publications with its live broadcasts and flags. */
	#syncPublications(peer: RemotePeer): void {
		const camera = peer.catalogs.get(KIND.camera);
		const screen = peer.catalogs.get(KIND.screen);
		const cameraBroadcast = peer.broadcasts.get(KIND.camera);
		const screenBroadcast = peer.broadcasts.get(KIND.screen);
		const want: Array<[Track.Source, boolean, boolean]> = [
			// source, published, muted
			[Track.Source.Microphone, !!cameraBroadcast, !(camera?.audio && peer.preview.audio !== false)],
			[Track.Source.Camera, !!camera?.video, false],
			[Track.Source.ScreenShare, !!screen?.video, false],
			[Track.Source.ScreenShareAudio, !!screen?.audio, false],
		];
		for (const [source, published, muted] of want) {
			let pub = peer.publications.get(source);
			if (published && !pub) {
				const isAudio = source === Track.Source.Microphone || source === Track.Source.ScreenShareAudio;
				const broadcast =
					source === Track.Source.Microphone || source === Track.Source.Camera ? cameraBroadcast : screenBroadcast;
				const control = isAudio ? broadcast?.audioControl() : broadcast?.videoControl();
				pub = new RemoteTrackPublication(
					isAudio ? Track.Kind.Audio : Track.Kind.Video,
					`${peer.participant.identity}/${source}`,
					source,
					source,
					control && {
						setSubscribed: (s) => control.setSubscribed(s),
						setVideoQuality: (q) => control.setVideoQuality(q),
						setVideoDimensions: (d) => control.setVideoDimensions(d),
					},
				);
				const created = pub;
				// setSubscribed() by the app changes isSubscribed; list views re-render on this.
				created.on('subscriptionStatusChanged', (status: SubscriptionStatus) => {
					peer.participant.emit(ParticipantEvent.TrackSubscriptionStatusChanged, created, status);
					this.emit(RoomEvent.TrackSubscriptionStatusChanged, created, status, peer.participant);
				});
				peer.publications.set(source, pub);
				peer.participant.addPublication(pub);
				peer.participant.emit(ParticipantEvent.TrackPublished, pub);
				this.emit(RoomEvent.TrackPublished, pub, peer.participant);
				if (!isAudio && broadcast) {
					this.#remoteVideo(
						peer,
						source === Track.Source.Camera ? KIND.camera : KIND.screen,
						broadcast.videoMediaTrack,
					);
				}
			} else if (!published && pub) {
				this.#unsubscribe(peer, pub);
				peer.publications.delete(source);
				peer.participant.removePublication(pub.trackSid);
				peer.participant.emit(ParticipantEvent.TrackUnpublished, pub);
				this.emit(RoomEvent.TrackUnpublished, pub, peer.participant);
				continue;
			}
			if (pub && pub.isMuted !== muted) {
				pub.setMuted(muted);
				const event = muted ? 'Muted' : 'Unmuted';
				peer.participant.emit(ParticipantEvent[`Track${event}`], pub);
				this.emit(RoomEvent[`Track${event}`], pub, peer.participant);
			}
		}
	}

	#subscribe(peer: RemotePeer, pub: RemoteTrackPublication, track: RemoteTrack): void {
		pub.setTrack(track);
		peer.participant.emit(ParticipantEvent.TrackSubscribed, track, pub);
		this.emit(RoomEvent.TrackSubscribed, track, pub, peer.participant);
		this.emit(RoomEvent.TrackSubscriptionStatusChanged, pub, pub.subscriptionStatus, peer.participant);
	}

	#unsubscribe(peer: RemotePeer, pub: RemoteTrackPublication): void {
		const track = pub.track;
		if (!track) return;
		pub.setTrack(undefined);
		track.detach();
		peer.participant.emit(ParticipantEvent.TrackUnsubscribed, track, pub);
		this.emit(RoomEvent.TrackUnsubscribed, track, pub, peer.participant);
		this.emit(RoomEvent.TrackSubscriptionStatusChanged, pub, pub.subscriptionStatus, peer.participant);
	}

	#remoteVideo(
		peer: RemotePeer,
		kind: Kind,
		media: MediaStreamTrack | undefined,
		dims?: {width: number; height: number},
	): void {
		const source = kind === KIND.camera ? Track.Source.Camera : Track.Source.ScreenShare;
		const pub = peer.publications.get(source);
		if (!pub) return;
		if (!media) {
			this.#unsubscribe(peer, pub);
			return;
		}
		let track = pub.videoTrack;
		if (!track) {
			const broadcast = peer.broadcasts.get(kind);
			track = new RemoteVideoTrack(media, pub.trackSid, {
				setVolume: () => {},
				getRTCStatsReport: async () => broadcast?.statsReport(),
			});
			track.source = source;
			this.#watchAttachments(peer, kind, track);
			this.#subscribe(peer, pub, track);
		}
		if (dims) {
			track.setDimensions(dims);
			pub.dimensions = dims;
		}
	}

	/** Decode video only while some element shows it (like adaptive stream). */
	#watchAttachments(peer: RemotePeer, kind: Kind, track: RemoteVideoTrack): void {
		const update = () => peer.broadcasts.get(kind)?.setVideoAttached(track.attachedElements.length > 0);
		track.on(TrackEvent.ElementAttached, update);
		track.on(TrackEvent.ElementDetached, update);
	}

	#remoteAudio(peer: RemotePeer, kind: Kind, media: MediaStreamTrack | undefined): void {
		const source = kind === KIND.camera ? Track.Source.Microphone : Track.Source.ScreenShareAudio;
		const pub = peer.publications.get(source);
		if (!pub) return;
		if (!media) {
			this.#unsubscribe(peer, pub);
			return;
		}
		const existing = pub.audioTrack;
		if (existing) {
			existing.setMediaStreamTrack(media);
			return;
		}
		const broadcast = peer.broadcasts.get(kind);
		const track = new RemoteAudioTrack(media, pub.trackSid, {
			setVolume: (v) => broadcast?.setVolume(v),
			getRTCStatsReport: async () => broadcast?.statsReport(),
			isMixed: () => broadcast?.mixed ?? false,
			setOutput: (volume, muted) => broadcast?.setOutput(volume, muted),
		});
		track.source = source;
		const volume = peer.participant.getVolume(source as Track.Source.Microphone | Track.Source.ScreenShareAudio);
		if (volume !== undefined) track.setVolume(volume);
		const sink = this.#activeDevices.get('audiooutput');
		if (sink) void track.setSinkId(sink);
		this.#subscribe(peer, pub, track);
	}

	#onData(peer: RemotePeer, message: DataMessage): void {
		if (message.to && !message.to.includes(this.localParticipant.identity)) return;
		const payload = fromBase64(message.payload);
		peer.participant.emit(ParticipantEvent.DataReceived, payload, undefined);
		this.emit(RoomEvent.DataReceived, payload, peer.participant, undefined, message.topic);
	}

	#updateActiveSpeakers(): void {
		const speakers: Array<Participant> = [];
		if (this.localParticipant.isSpeaking) speakers.push(this.localParticipant);
		for (const p of this.remoteParticipants.values()) if (p.isSpeaking) speakers.push(p);
		speakers.sort((a, b) => b.audioLevel - a.audioLevel);
		const same =
			speakers.length === this.activeSpeakers.length && speakers.every((p, i) => p === this.activeSpeakers[i]);
		if (same) return;
		this.activeSpeakers = speakers;
		this.emit(RoomEvent.ActiveSpeakersChanged, speakers);
	}

	async switchActiveDevice(kind: MediaDeviceKind, deviceId: string, _exact = false): Promise<boolean> {
		this.#activeDevices.set(kind, deviceId);
		if (kind === 'audiooutput') {
			for (const p of this.remoteParticipants.values()) {
				for (const pub of p.audioTrackPublications.values()) await pub.audioTrack?.setSinkId(deviceId);
			}
		} else {
			const source = kind === 'audioinput' ? Track.Source.Microphone : Track.Source.Camera;
			const track = this.localParticipant.getTrackPublication(source)?.track;
			await track?.restartTrack({deviceId: {exact: deviceId}});
		}
		this.emit(RoomEvent.ActiveDeviceChanged, kind, deviceId);
		return true;
	}

	getActiveDevice(kind: MediaDeviceKind): string | undefined {
		return this.#activeDevices.get(kind);
	}

	async startAudio(): Promise<void> {
		this.canPlaybackAudio = true;
		this.emit(RoomEvent.AudioPlaybackStatusChanged, true);
	}

	/**
	 * Turns media encryption on or off (Fluxer turns it off while a
	 * participant without E2EE is in the room). Like LiveKit, only rooms
	 * created with an e2ee key provider can enable it.
	 */
	async setE2EEEnabled(enabled: boolean): Promise<void> {
		if (enabled && !this.#crypto.provider) {
			throw new Error('e2ee not configured, please set e2ee settings within the room options');
		}
		this.#crypto.enabled = enabled;
		this.isE2EEEnabled = enabled;
	}

	getParticipantByIdentity(identity: string): Participant | undefined {
		if (identity === this.localParticipant.identity) return this.localParticipant;
		return this.remoteParticipants.get(identity);
	}

	#teardown(): void {
		this.#unregisterKeys?.();
		this.#unregisterKeys = undefined;
		this.#statusDispose?.();
		this.#statusDispose = undefined;
		this.engine.client.rtt = undefined;
		this.#discovery?.close();
		this.#discovery = undefined;
		// Fade everyone we hear out instead of cutting them off.
		for (const peer of this.#peers.values()) for (const b of peer.broadcasts.values()) b.closeGracefully();
		this.#peers.clear();
		this.#departed.clear();
		this.#local?.close();
		this.#local = undefined;
		this.localParticipant.publisher = undefined;
		// Closing the connection tears the remote audio down at once; let the
		// fade-outs above finish first.
		const connection = this.#connection;
		this.#connection = undefined;
		if (connection) setTimeout(() => connection.close(), 100);
	}

	async disconnect(stopTracks = true): Promise<void> {
		if (this.state === ConnectionState.Disconnected && !this.#connection) return;
		for (const participant of [...this.remoteParticipants.values()]) {
			this.remoteParticipants.delete(participant.identity);
			this.emit(RoomEvent.ParticipantDisconnected, participant);
		}
		if (stopTracks) {
			for (const pub of this.localParticipant.getTrackPublications()) pub.track?.stop();
		}
		this.#teardown();
		this.#setState(ConnectionState.Disconnected);
		this.emit(RoomEvent.Disconnected, DisconnectReason.CLIENT_INITIATED);
	}
}

// Only the camera broadcast carries the participant's voice.
function k(kind: Kind): boolean {
	return kind === KIND.camera;
}
